import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Loader2, Upload, ChevronUp, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { RichTextEditor } from '../../components/RichTextEditor';
import { api, fileUrl, uploadFile } from '../../lib/api';
import { toast } from 'sonner';

export const ImageField = ({ value, onChange, category = 'general', accept = 'image/*,.pdf,.doc,.docx,.mp4,.webm' }) => {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file, category);
      onChange(res.url);
      toast.success('File uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  const isImage = value && /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(value.split('?')[0]);
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Paste URL or upload →" className="h-10 text-sm" />
        <label className="h-10 px-3 inline-flex items-center gap-1.5 rounded-lg bg-[#EAF2FF] border border-[#CFE0FF] text-[#1a2744] text-sm font-bold cursor-pointer hover:bg-[#dcebff]">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
          <input type="file" accept={accept} className="hidden" onChange={handleUpload} />
        </label>
      </div>
      {isImage && (value.startsWith('http') || value.startsWith('/api')) && <img src={fileUrl(value)} alt="Preview" className="h-24 rounded-lg object-cover border" />}
    </div>
  );
};

const DataPointsField = ({ value = [], onChange }) => {
  const points = value.length ? value : [{ label: '', value: 0 }];
  const update = (i, k, v) => {
    const copy = points.map((p, j) => (j === i ? { ...p, [k]: k === 'value' ? parseFloat(v) || 0 : v } : p));
    onChange(copy);
  };
  return (
    <div className="space-y-2">
      {points.map((p, i) => (
        <div key={i} className="flex gap-2">
          <Input value={p.label} onChange={(e) => update(i, 'label', e.target.value)} placeholder="Label (e.g. 2025)" className="h-10 text-sm" />
          <Input type="number" step="any" value={p.value} onChange={(e) => update(i, 'value', e.target.value)} placeholder="Value" className="h-10 text-sm w-32" />
          <button type="button" onClick={() => onChange(points.filter((_, j) => j !== i))} className="text-red-600 p-2" aria-label="Remove point"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...points, { label: '', value: 0 }])} className="font-bold"><Plus className="h-3.5 w-3.5 mr-1" /> Add point</Button>
    </div>
  );
};

// Select whose options load from an admin API endpoint (e.g. link a Newsroom
// article or Council meeting to a chart).
export const DynamicSelect = ({ field, value, onChange }) => {
  const [options, setOptions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get(field.endpoint).then((r) => {
      const items = r.data || [];
      setOptions(items.map((it) => {
        const isPublished = field.statusKey ? (it[field.statusKey] === 'published' || it[field.statusKey] === true) : true;
        return {
          value: String(it[field.valueKey || 'id']),
          label: `${it[field.labelKey || 'title'] || '(untitled)'}${isPublished ? '' : ' — DRAFT (not public yet)'}`,
        };
      }));
    }).catch(() => {}).finally(() => setLoaded(true));
  }, [field.endpoint, field.valueKey, field.labelKey, field.statusKey]);
  return (
    <Select value={value || 'none'} onValueChange={(v) => onChange(v === 'none' ? '' : v)}>
      <SelectTrigger className="h-10" data-testid={`field-${field.name}`}>
        <SelectValue placeholder={field.placeholder || 'Select…'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">{field.noneLabel || '— None connected —'}</SelectItem>
        {loaded && options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

const CHART_TYPE_OPTIONS = [
  { value: 'bar', label: 'Bar chart' }, { value: 'stacked_bar', label: 'Stacked bar chart' },
  { value: 'line', label: 'Line chart' }, { value: 'timeline', label: 'Timeline chart' },
  { value: 'area', label: 'Area chart' }, { value: 'pie', label: 'Pie chart' }, { value: 'doughnut', label: 'Donut chart' },
  { value: 'stat_cards', label: 'Number / stat cards' }, { value: 'comparison_cards', label: 'Comparison cards' },
  { value: 'table', label: 'Table with source notes' },
];

const BLOCK_REVIEW_OPTIONS = [
  { value: 'draft', label: 'Draft' }, { value: 'needs_review', label: 'Needs Review' },
  { value: 'source_based', label: 'Source-Based' }, { value: 'verified', label: 'Verified' },
];

const newBlock = () => ({
  id: `blk-${Date.now()}-${Math.floor(Math.random() * 9999)}`,
  type: 'dataset', dataset_id: '', title: '', subtitle: '', chart_type: 'bar',
  data_points: [], unit: '', image_url: '', description: '', alt_text: '',
  source_name: '', source_url: '', last_updated: '', data_notes: '',
  public: true, review_status: 'draft',
});

// Inline data points editor for chart blocks (supports optional 2nd series for
// stacked bar / comparison charts).
const BlockPointsField = ({ value = [], onChange, showSecond }) => {
  const points = value.length ? value : [{ label: '', value: 0 }];
  const update = (i, k, v) => onChange(points.map((p, j) => (j === i ? { ...p, [k]: (k === 'value' || k === 'value2') ? (v === '' ? '' : parseFloat(v) || 0) : v } : p)));
  return (
    <div className="space-y-2">
      {points.map((p, i) => (
        <div key={i} className="flex gap-2">
          <Input value={p.label} onChange={(e) => update(i, 'label', e.target.value)} placeholder="Label" className="h-9 text-sm" />
          <Input type="number" step="any" value={p.value} onChange={(e) => update(i, 'value', e.target.value)} placeholder="Value" className="h-9 text-sm w-24" />
          {showSecond && <Input type="number" step="any" value={p.value2 ?? ''} onChange={(e) => update(i, 'value2', e.target.value)} placeholder="Value 2" className="h-9 text-sm w-24" />}
          <button type="button" onClick={() => onChange(points.filter((_, j) => j !== i))} className="text-red-600 p-1.5" aria-label="Remove point"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...points, { label: '', value: 0 }])} className="font-bold h-8"><Plus className="h-3.5 w-3.5 mr-1" /> Add point</Button>
    </div>
  );
};

// Chart blocks editor used inside the Newsroom article editor.
// L stays at module scope so it isn't recreated on every keystroke.
const L = ({ children }) => <label className="block text-xs font-bold text-slate-600 mb-1">{children}</label>;

export const ChartBlocksField = ({ value = [], onChange }) => {
  const blocks = value || [];
  const setBlock = (i, patch) => onChange(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const copy = [...blocks];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  return (
    <div className="space-y-3" data-testid="chart-blocks-editor">
      {blocks.map((b, i) => (
        <div key={b.id || i} className="border border-[#CFE0FF] bg-[#F7FAFF] rounded-xl p-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-black uppercase tracking-wide text-[#1a2744]">Chart block {i + 1}</p>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => move(i, -1)} className="p-1.5 rounded hover:bg-white text-slate-500" aria-label="Move up"><ChevronUp className="h-4 w-4" /></button>
              <button type="button" onClick={() => move(i, 1)} className="p-1.5 rounded hover:bg-white text-slate-500" aria-label="Move down"><ChevronDown className="h-4 w-4" /></button>
              <button type="button" onClick={() => onChange(blocks.filter((_, j) => j !== i))} className="p-1.5 rounded hover:bg-red-50 text-red-600" aria-label="Remove block" data-testid="chart-block-remove"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <L>Block type</L>
              <Select value={b.type || 'dataset'} onValueChange={(v) => setBlock(i, { type: v })}>
                <SelectTrigger className="h-9 bg-white" data-testid="chart-block-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dataset">Existing City Data chart / dataset</SelectItem>
                  <SelectItem value="inline">Simple chart (built in this article)</SelectItem>
                  <SelectItem value="image">Chart image upload / safe embed screenshot</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {b.type === 'dataset' && (
              <div>
                <L>City Data chart</L>
                <DynamicSelect field={{ name: `block-${i}-dataset`, endpoint: '/admin/datasets', statusKey: 'published', noneLabel: '— Choose a chart —' }} value={b.dataset_id} onChange={(v) => setBlock(i, { dataset_id: v })} />
                <p className="text-[11px] text-slate-400 mt-1">Only published charts appear publicly; a draft chart embed stays hidden.</p>
              </div>
            )}
            {b.type === 'inline' && (
              <div>
                <L>Chart type</L>
                <Select value={b.chart_type || 'bar'} onValueChange={(v) => setBlock(i, { chart_type: v })}>
                  <SelectTrigger className="h-9 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{CHART_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          {b.type === 'inline' && (
            <>
              <div><L>Data points{['stacked_bar', 'comparison_cards'].includes(b.chart_type) ? ' (Value 2 = second series)' : ''}</L>
                <BlockPointsField value={b.data_points} onChange={(v) => setBlock(i, { data_points: v })} showSecond={['stacked_bar', 'comparison_cards', 'bar', 'line'].includes(b.chart_type)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><L>Unit</L><Input value={b.unit || ''} onChange={(e) => setBlock(i, { unit: e.target.value })} placeholder="$M, %, …" className="h-9 bg-white" /></div>
              </div>
            </>
          )}
          {b.type === 'image' && (
            <div><L>Chart image</L><ImageField value={b.image_url} onChange={(v) => setBlock(i, { image_url: v })} category="charts" /></div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><L>Chart title</L><Input value={b.title || ''} onChange={(e) => setBlock(i, { title: e.target.value })} className="h-9 bg-white" data-testid="chart-block-title-input" /></div>
            <div><L>Subtitle</L><Input value={b.subtitle || ''} onChange={(e) => setBlock(i, { subtitle: e.target.value })} className="h-9 bg-white" /></div>
            <div><L>Source name</L><Input value={b.source_name || ''} onChange={(e) => setBlock(i, { source_name: e.target.value })} className="h-9 bg-white" /></div>
            <div><L>Source link</L><Input value={b.source_url || ''} onChange={(e) => setBlock(i, { source_url: e.target.value })} className="h-9 bg-white" /></div>
            <div><L>Last updated</L><Input type="date" value={b.last_updated || ''} onChange={(e) => setBlock(i, { last_updated: e.target.value })} className="h-9 bg-white" /></div>
            <div><L>Alt text (accessibility)</L><Input value={b.alt_text || ''} onChange={(e) => setBlock(i, { alt_text: e.target.value })} className="h-9 bg-white" /></div>
          </div>
          <div><L>Chart description (shown under the chart)</L><Textarea value={b.description || ''} onChange={(e) => setBlock(i, { description: e.target.value })} rows={2} className="bg-white text-sm" /></div>
          <div><L>Data notes</L><Textarea value={b.data_notes || ''} onChange={(e) => setBlock(i, { data_notes: e.target.value })} rows={2} className="bg-white text-sm" /></div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs font-bold text-slate-600">Public
              <Switch checked={b.public !== false} onCheckedChange={(v) => setBlock(i, { public: v })} data-testid="chart-block-public" />
            </label>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-600">Review:</span>
              <Select value={b.review_status || 'draft'} onValueChange={(v) => setBlock(i, { review_status: v })}>
                <SelectTrigger className="h-8 w-40 bg-white text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{BLOCK_REVIEW_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => onChange([...blocks, newBlock()])} className="font-bold w-full border-dashed" data-testid="chart-block-add">
        <Plus className="h-4 w-4 mr-1.5" /> Add chart block
      </Button>
    </div>
  );
};

export const FieldInput = ({ field, value, onChange }) => {
  switch (field.type) {
    case 'textarea':
      return <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={field.rows || 3} placeholder={field.placeholder} data-testid={`field-${field.name}`} />;
    case 'richtext':
      return <RichTextEditor value={value} onChange={onChange} />;
    case 'select':
      return (
        <Select value={value ?? ''} onValueChange={onChange}>
          <SelectTrigger className="h-10" data-testid={`field-${field.name}`}><SelectValue placeholder={field.placeholder || 'Select…'} /></SelectTrigger>
          <SelectContent>{field.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      );
    case 'switch':
      return <Switch checked={!!value} onCheckedChange={onChange} data-testid={`field-${field.name}`} />;
    case 'image':
      return <ImageField value={value} onChange={onChange} category={field.category || 'general'} />;
    case 'list':
      return <Textarea value={(value || []).join('\n')} onChange={(e) => onChange(e.target.value.split('\n'))} rows={field.rows || 4} placeholder="One item per line" data-testid={`field-${field.name}`} />;
    case 'datapoints':
      return <DataPointsField value={value} onChange={onChange} />;
    case 'dynamic_select':
      return <DynamicSelect field={field} value={value} onChange={onChange} />;
    case 'chartblocks':
      return <ChartBlocksField value={value} onChange={onChange} />;
    case 'heading':
      return <div className="border-t border-slate-200 pt-2 -mb-1"><p className="text-[11px] text-slate-400">{field.placeholder || ''}</p></div>;
    case 'readonly':
      return <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap">{value || '—'}</div>;
    case 'number':
      return <Input type="number" step="any" value={value ?? ''} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} className="h-10" data-testid={`field-${field.name}`} />;
    case 'date':
      return <Input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} className="h-10" data-testid={`field-${field.name}`} />;
    default:
      return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className="h-10" data-testid={`field-${field.name}`} />;
  }
};

const REVIEW_BADGES = {
  sample: { label: 'Sample', cls: 'bg-amber-100 text-amber-900 border-amber-300' },
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  needs_review: { label: 'Needs Review', cls: 'bg-[#FFF7E6] text-[#7A4E00] border-[#F2D08A]' },
  source_based: { label: 'Source-Based', cls: 'bg-sky-50 text-sky-800 border-sky-200' },
  verified: { label: 'Verified', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
};

export const ReviewBadge = ({ status }) => {
  const b = REVIEW_BADGES[status];
  if (!b) return <span className="text-xs text-slate-300">—</span>;
  return <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-bold ${b.cls}`} data-testid={`review-badge-${status}`}>{b.label}</span>;
};

export const AdminCrud = ({ title, description, listEndpoint, createEndpoint, updateEndpoint, deleteEndpoint, fields, columns, searchKeys = ['title'], allowCreate = true, allowDelete = true, reviewable = false, rowActions = [] }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reviewFilter, setReviewFilter] = useState('all');
  const [editing, setEditing] = useState(null); // null | {} (new) | item
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    api.get(listEndpoint).then((r) => setItems(r.data || [])).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [listEndpoint]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const defaults = {};
    effFields.forEach((f) => { defaults[f.name] = f.default !== undefined ? f.default : (f.type === 'switch' ? true : f.type === 'datapoints' || f.type === 'list' ? [] : ''); });
    setForm(defaults);
    setEditing({});
  };

  const openEdit = (item) => { setForm({ ...item }); setEditing(item); };

  const save = async () => {
    setBusy(true);
    try {
      if (editing?.id) {
        await api.put(`${updateEndpoint}/${editing.id}`, form);
        toast.success('Saved');
      } else {
        await api.post(createEndpoint, form);
        toast.success('Created');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`${deleteEndpoint}/${deleting.id}`);
      toast.success('Deleted');
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = items.filter((i) =>
    (!search || searchKeys.some((k) => String(i[k] || '').toLowerCase().includes(search.toLowerCase()))) &&
    (!reviewable || reviewFilter === 'all' || (i.review_status || 'none') === reviewFilter)
  );

  const effColumns = reviewable
    ? [...columns, { key: 'review_status', label: 'Review', render: (i) => <ReviewBadge status={i.review_status} /> }]
    : columns;
  const effFields = reviewable
    ? [...fields, { name: 'review_status', label: 'Review status', type: 'select', default: 'verified', options: [
        { value: 'sample', label: 'Sample (placeholder content)' },
        { value: 'draft', label: 'Draft' },
        { value: 'needs_review', label: 'Needs Review' },
        { value: 'source_based', label: 'Source-Based (official source attached)' },
        { value: 'verified', label: 'Verified' },
      ], help: 'Mark seeded/sample content as Verified once you have reviewed and corrected it.' }]
    : fields;

  return (
    <div data-testid={`admin-crud-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">{title}</h1>
        {allowCreate && (
          <Button onClick={openNew} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="crud-add-button"><Plus className="h-4 w-4 mr-1.5" /> Add</Button>
        )}
      </div>
      {description && <p className="text-sm text-slate-500 mb-4">{description}</p>}

      <div className="bg-[#EAF2FF] border border-[#CFE0FF] rounded-xl p-3 mb-4 flex flex-col sm:flex-row gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="h-10 bg-white sm:max-w-sm" data-testid="crud-search-input" />
        {reviewable && (
          <Select value={reviewFilter} onValueChange={setReviewFilter}>
            <SelectTrigger className="h-10 bg-white sm:w-52" data-testid="crud-review-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All review statuses</SelectItem>
              <SelectItem value="sample">Sample only</SelectItem>
              <SelectItem value="needs_review">Needs Review only</SelectItem>
              <SelectItem value="verified">Verified only</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Nothing here yet.</CardContent></Card>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {effColumns.map((c) => <th key={c.key} className="text-left font-bold text-slate-600 px-4 py-3">{c.label}</th>)}
                  <th className="px-4 py-3 w-24" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid="crud-row">
                    {effColumns.map((c) => <td key={c.key} className="px-4 py-3 max-w-xs truncate">{c.render ? c.render(item) : String(item[c.key] ?? '')}</td>)}
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        {rowActions.map((a, ai) => (
                          <button key={a.label || a.testId || ai} onClick={() => a.onClick(item, load)} title={a.label} className="p-2 rounded-lg hover:bg-[#FFF7E6] text-[#8a6f22]" aria-label={a.label} data-testid={a.testId || `crud-row-action-${ai}`}>{a.icon}</button>
                        ))}
                        <button onClick={() => openEdit(item)} className="p-2 rounded-lg hover:bg-[#EAF2FF] text-[#1a2744]" aria-label="Edit" data-testid="crud-edit-button"><Pencil className="h-4 w-4" /></button>
                        {allowDelete && <button onClick={() => setDeleting(item)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" aria-label="Delete" data-testid="crud-delete-button"><Trash2 className="h-4 w-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filtered.map((item) => (
              <Card key={item.id} data-testid="crud-card">
                <CardContent className="p-4">
                  {reviewable && <div className="mb-1.5"><ReviewBadge status={item.review_status} /></div>}
                  {effColumns.filter((c) => c.key !== 'review_status').slice(0, 3).map((c) => (
                    <div key={c.key} className="flex justify-between gap-3 py-0.5">
                      <span className="text-xs font-bold text-slate-400 uppercase">{c.label}</span>
                      <span className="text-sm text-right text-slate-800 truncate">{c.render ? c.render(item) : String(item[c.key] ?? '')}</span>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-3">
                    <Button variant="outline" size="sm" onClick={() => openEdit(item)} className="flex-1 font-bold"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                    {rowActions.map((a, ai) => (
                      <Button key={a.label || a.testId || ai} variant="outline" size="sm" onClick={() => a.onClick(item, load)} className="font-bold text-[#8a6f22] border-[#F2D08A]" data-testid={a.testId || `crud-row-action-m-${ai}`}>{a.icon}</Button>
                    ))}
                    {allowDelete && <Button variant="outline" size="sm" onClick={() => setDeleting(item)} className="text-red-600 border-red-200 font-bold"><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Edit dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="crud-edit-dialog">
          <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">{editing?.id ? `Edit ${title}` : `New ${title}`}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {effFields.map((f) => (
              <div key={f.name}>
                <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">{f.label}</label>
                <FieldInput field={f} value={form[f.name]} onChange={(v) => setForm((prev) => ({ ...prev, [f.name]: v }))} />
                {f.help && <p className="text-xs text-slate-400 mt-1">{f.help}</p>}
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <Button onClick={save} disabled={busy} className="flex-1 h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="crud-save-button">
                {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Save
              </Button>
              <Button variant="outline" onClick={() => setEditing(null)} className="h-11 font-bold">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="crud-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this item?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="crud-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
